import { PlatformEarningsDetails } from '@/components/reports/PlatformEarningsBreakdown'
import ReportDateRangeFilterBar, {
  type ReportDateRangeSelection,
} from '@/components/reports/ReportDateRangeFilterBar'
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { StyledIcon } from '@/components/ui/styled-icon'
import { Typography } from '@/components/ui/typography'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { CheckoutStep } from '@/lib/enums/CheckoutStep'
import {
  REPORT_DATE_MODE,
  getCurrentReportDateFilters,
  getOrganizationReportDateBounds,
} from '@/lib/report-date-range'
import { getOrganizationNow } from '@/lib/reporting-dates'
import { useOrganization } from '@/lib/services/organizations/organizations.query'
import { platformLedgerQueries } from '@/lib/services/platform-ledger/platform-ledger.query'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import {
  canDisplaySalesReport,
  isIncompleteSalesReport,
  moneyQueries,
} from '@/lib/services/money/money.query'
import { formatCurrency, formatNumber, formatPercent, unslugify } from '@/lib/utils'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  Activity,
  DollarSign,
  Funnel,
  PersonStanding,
  ShieldAlert,
  ShoppingCart,
  SquareCheckBig,
} from 'lucide-react'
import type { Dayjs } from 'dayjs'
import { useState } from 'react'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import TimezoneInfo from '../TimezoneInfo'

function formatDashboardDateRange(startDay: Dayjs, endDay: Dayjs) {
  if (startDay.isSame(endDay, 'day')) {
    return startDay.format('MMM D, YYYY')
  }

  if (startDay.isSame(endDay, 'year')) {
    return `${startDay.format('MMM D')} - ${endDay.format('MMM D, YYYY')}`
  }

  return `${startDay.format('MMM D, YYYY')} - ${endDay.format('MMM D, YYYY')}`
}

interface DashboardCardProps {
  orgId: number
  startDate: string
  endDate: string
  monthLabel: string
  orderDateRangeText: string
}

// Loads on its own so the slow ledger summary doesn't hold back the rest of the dashboard.
function RevenueDashboardCard({
  endDate,
  monthLabel,
  orderDateRangeText,
  orgId,
  startDate,
}: DashboardCardProps) {
  const path = usePathHelper()
  const { data, isError, isPending } = useQuery(
    platformLedgerQueries.summary({
      organizationId: orgId,
      filters: { start_date: startDate, end_date: endDate },
    })
  )
  const report = isError ? undefined : data

  return (
    <Card interactive className="@container/card relative h-full">
      <CardHeader>
        <CardDescription className="flex items-center gap-1.5">
          <Link
            {...path.platformLedger.index().using({ orgId }).search({
              start_date: startDate,
              end_date: endDate,
            }).link}
            className="after:absolute after:inset-0 after:content-['']"
          >
            Platform Revenue
          </Link>
          {/* Keep help above the card link without nesting a button inside it. */}
          <span className="relative">
            <PlatformEarningsDetails
              summary={report?.summary}
              coverage={report?.processing_cost_coverage}
            />
          </span>
        </CardDescription>
        <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
          {report
            ? formatCurrency(report.summary.net_revenue)
            : isPending
              ? 'Loading…'
              : 'Unavailable'}
        </CardTitle>
        <CardAction>
          <StyledIcon icon={DollarSign} tone="success" />
        </CardAction>
      </CardHeader>
      <CardFooter className="flex-col items-start gap-1.5 text-sm">
        <div className="line-clamp-1 flex gap-1 font-medium">
          {`Platform Revenue ${monthLabel} (${orderDateRangeText})`}
        </div>
        <div className="text-muted-foreground">
          {isError
            ? 'Platform Revenue couldn’t be loaded. Open the platform ledger to try again.'
            : 'Click here to inspect the platform ledger for this period.'}
        </div>
      </CardFooter>
    </Card>
  )
}

function SalesDashboardCard({
  orgId,
  startDate,
  endDate,
  monthLabel,
  orderDateRangeText,
}: DashboardCardProps) {
  const path = usePathHelper()
  const { data, error, isError, isPending } = useQuery(
    moneyQueries.sales({
      organizationId: orgId,
      filters: { start_date: startDate, end_date: endDate },
    })
  )
  const showSales = canDisplaySalesReport({ data, error, isError })
  return (
    <Link
      {...path.salesSummary
        .index()
        .using({ orgId })
        .search({ start_date: startDate, end_date: endDate }).link}
      className="h-full"
    >
      <Card interactive className="@container/card h-full">
        <CardHeader>
          <CardDescription>Sales</CardDescription>
          <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
            {showSales && data
              ? formatCurrency(data.summary.net_sales)
              : isPending
                ? 'Loading…'
                : 'Unavailable'}
          </CardTitle>
          <CardAction>
            <StyledIcon icon={DollarSign} tone="success" />
          </CardAction>
        </CardHeader>
        <CardFooter className="flex-col items-start gap-1.5 text-sm">
          <Typography variant="label">
            Sales {monthLabel} ({orderDateRangeText})
          </Typography>
          {isError ? (
            <Typography variant="description">
              {showSales
                ? 'Showing the last loaded sales. Open Sales to try again.'
                : isIncompleteSalesReport(error)
                  ? 'An order needs attention. Open Sales for details.'
                  : 'Sales couldn’t be loaded. Open Sales to try again.'}
            </Typography>
          ) : null}
        </CardFooter>
      </Card>
    </Link>
  )
}

const Analytics = () => {
  const path = usePathHelper()
  const ability = useAbility()
  const orgId = useRouteOrganizationId()
  const { data: organization } = useOrganization(orgId)
  const isGlobalOrg = useIsGlobalOrg()
  const canViewTransactions = ability.can('list', 'transactions')

  const organizationNow = getOrganizationNow(organization?.timezone)
  const { minDate, maxDate } = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )

  // ----- Initial range: current month to date (org timezone) -----
  const { start_date: initialStart, end_date: initialEnd } =
    getCurrentReportDateFilters(organizationNow)

  const [selectedRange, setSelectedRange] = useState({
    start_date: initialStart,
    end_date: initialEnd,
  })

  const startDay = dayjs(selectedRange.start_date)
  const endDay = dayjs(selectedRange.end_date)
  const today = organizationNow.startOf('day')

  const startDate = startDay.format('YYYY-MM-DD')
  const endDate = endDay.format('YYYY-MM-DD')

  // Is the range exactly one full calendar month?
  const isFullCalendarMonth =
    startDay.isSame(startDay.startOf('month'), 'day') &&
    endDay.isSame(startDay.endOf('month'), 'day') &&
    startDay.isSame(endDay, 'month')
  const isCurrentMonthToDate =
    startDay.isSame(today.startOf('month'), 'day') && endDay.isSame(today, 'day')

  const isCurrentMonthRange =
    startDay.isSame(today, 'month') && (isFullCalendarMonth || isCurrentMonthToDate)

  const monthLabel = isFullCalendarMonth
    ? isCurrentMonthRange
      ? 'this month'
      : `in ${startDay.format('MMMM YYYY')}`
    : isCurrentMonthRange
      ? 'this month'
      : 'during this period'

  const orderDateRangeText = isFullCalendarMonth
    ? startDay.format('MMM YYYY')
    : formatDashboardDateRange(startDay, endDay)

  const periodDescription = isCurrentMonthRange
    ? formatDashboardDateRange(startDay, today)
    : orderDateRangeText

  // ----- Data fetching -----

  const { data: analytics } = useSuspenseQuery(
    salesOrderQueries.analytics({
      organizationId: orgId,
      filters: {
        start_date: startDate,
        end_date: endDate,
      },
    })
  )

  const funnelBaseTotal = analytics.funnel[1]?.count || 0

  const handleDateRangeApply = ({ start_date, end_date }: ReportDateRangeSelection) => {
    setSelectedRange({ start_date, end_date })
  }

  return (
    <>
      {/* Sales Snapshot header + date picker */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 shrink-0">
          <Typography variant="h3" className="flex items-center gap-2">
            <ShoppingCart /> Sales Snapshot
          </Typography>
        </div>
        <ReportDateRangeFilterBar
          appliedDateMode={REPORT_DATE_MODE.CUSTOM}
          initialFilters={{
            ...selectedRange,
            minDate,
            maxDate,
          }}
          now={organizationNow}
          onApply={handleDateRangeApply}
        />
      </div>

      {/* Sales Snapshot cards */}
      <div className="flex flex-1 flex-col mb-6">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="grid grid-cols-1 gap-5 @xl/main:grid-cols-2">
            <Link
              to={path.salesOrders.index().href}
              search={{
                created_range: `${startDate}to${endDate}`,
              }}
              className="h-full"
            >
              <Card interactive className="@container/card h-full">
                <CardHeader>
                  <CardDescription>New Orders</CardDescription>
                  <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                    {formatNumber(analytics.orders_created)}
                  </CardTitle>
                  <CardAction>
                    <StyledIcon icon={ShoppingCart} tone="periwinkle" />
                  </CardAction>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1.5 text-sm">
                  <div className="line-clamp-1 flex gap-2 font-medium">
                    Orders received {monthLabel} ({orderDateRangeText})
                  </div>
                  <div className="text-muted-foreground">
                    Click here to see a list of orders for {periodDescription}.
                  </div>
                </CardFooter>
              </Card>
            </Link>

            {!isGlobalOrg && canViewTransactions ? (
              <SalesDashboardCard
                orgId={orgId}
                startDate={startDate}
                endDate={endDate}
                monthLabel={monthLabel}
                orderDateRangeText={orderDateRangeText}
              />
            ) : null}

            {isGlobalOrg && canViewTransactions ? (
              <RevenueDashboardCard
                endDate={endDate}
                monthLabel={monthLabel}
                orderDateRangeText={orderDateRangeText}
                orgId={orgId}
                startDate={startDate}
              />
            ) : null}

            <Link
              to={path.salesOrders.index().href}
              search={{ fulfillment_status_filter: 'to_do' }}
              className="h-full"
            >
              <Card interactive className="@container/card h-full">
                <CardHeader>
                  <CardDescription>Pending Fulfillment</CardDescription>
                  <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                    {formatNumber(analytics.orders_pending)}
                  </CardTitle>
                  <CardAction>
                    <StyledIcon icon={SquareCheckBig} tone="warning" />
                  </CardAction>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1.5 text-sm">
                  <div className="line-clamp-1 flex gap-2 font-medium">
                    Orders that still need to be fulfilled
                  </div>
                  <div className="text-muted-foreground">
                    This is your to-do list.{' '}
                    {analytics.orders_pending
                      ? 'Click here to get started'
                      : "Looks like you're all caught up!"}
                  </div>
                </CardFooter>
              </Card>
            </Link>

            <Link
              to={path.salesOrders.index().href}
              search={{ filter: 'past_due' }}
              className="h-full"
            >
              <Card interactive className="@container/card h-full">
                <CardHeader>
                  <CardDescription>Delivery / Pickup Date Passed</CardDescription>
                  <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                    {formatNumber(analytics.orders_past_due)}
                  </CardTitle>
                  <CardAction>
                    <StyledIcon icon={ShieldAlert} tone="destructive" />
                  </CardAction>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1.5 text-sm">
                  <div className="line-clamp-1 flex gap-2 font-medium">
                    Orders that should already be marked picked up or delivered
                  </div>
                  <div className="text-muted-foreground">
                    {analytics.orders_past_due
                      ? 'Click here to review and mark completed orders.'
                      : 'No delivery or pickup dates have passed.'}
                  </div>
                </CardFooter>
              </Card>
            </Link>
          </div>
        </div>
      </div>

      {/* Traffic section */}
      <div>
        <Typography variant="h3" className="flex items-center gap-2 mb-4">
          <Activity /> Traffic
        </Typography>

        <div className="flex flex-1 flex-col mb-6">
          <div className="@container/main flex flex-1 flex-col gap-2">
            <div className="grid grid-cols-1 gap-5 @xl/main:grid-cols-2">
              <Card className="@container/card h-full">
                <CardHeader>
                  <CardDescription>Unique Visitors</CardDescription>
                  <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
                    {formatNumber(analytics.unique_visitors)}
                  </CardTitle>
                  <CardAction>
                    <StyledIcon icon={PersonStanding} tone="brand" />
                  </CardAction>
                </CardHeader>
                <CardFooter className="flex-col items-start gap-1.5 text-sm">
                  <div className="line-clamp-1 flex gap-2 font-medium">
                    Unique visitors {monthLabel} ({orderDateRangeText})
                  </div>
                  <div className="text-muted-foreground">
                    We periodically remove bot and search-engine visits, so this number may dip
                    slightly as we keep it focused on real shoppers.
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Conversion Funnel */}
      <div>
        <div>
          <Typography variant="h3" className="flex items-center gap-2 mb-4">
            <Funnel /> Conversion Funnel{' '}
            <IfGlobalOrg>
              <Typography variant="supporting">(All Organizations)</Typography>
            </IfGlobalOrg>
            <IfGlobalOrg show={false}>
              <Typography variant="supporting">(Web Orders Only)</Typography>
            </IfGlobalOrg>
          </Typography>
          <Card>
            <CardHeader>
              <div className="space-y-3">
                {analytics.funnel.length > 0 ? (
                  analytics.funnel.map((step) => {
                    if (step.step === CheckoutStep.Visit.Value) return null

                    const ratio = step.count / (funnelBaseTotal || 1)
                    const percentText = formatPercent(ratio, 1)

                    return (
                      <div key={step.step} className="space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <Typography variant="label">{unslugify(step.step)}</Typography>
                          <div>
                            <Typography variant="supporting" weight="bold">
                              {formatNumber(step.count)}
                            </Typography>{' '}
                            <Typography variant="supporting">({percentText})</Typography>
                          </div>
                        </div>
                        <div className="h-2 w-full rounded-full bg-card ring-1 ring-inset ring-input">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{
                              width: percentText,
                            }}
                          />
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <Typography variant="supporting" className="flex justify-center">
                    No data available
                  </Typography>
                )}
              </div>
            </CardHeader>
          </Card>
        </div>
      </div>
      <div className="mt-4">
        <TimezoneInfo />
      </div>
    </>
  )
}

export default Analytics
